import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Invoice } from "./InvoiceTypes";
import ApiRest from "../../../service/ApiRest";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { NaePaging, NaeTable } from 'nae-react-core-styles'
import ApiInvoice from "../../../service/ApiInvoice";
import DateField from "../../Forms/fields/DateField";

const moduleName = 'invoice';

interface Props {
    options?: any
    replaceModule?: string,
}

export default function InvoicesListCard(props: Props) {
    const history = useHistory();

    const cardRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef<{[key: string]: HTMLTableRowElement}>({});
    const clickedRowId = useRef<string | null>(null);
    const restorePositionAfterLoad = useRef<boolean>(false);
    const dataLoadComplete = useRef<boolean>(false);
    const invoicesRef = useRef<Invoice[]>([]);
    
    const [readyToLoad, setReadyToLoad] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [state, setState] = useState('');
    const [activePage, setActivePage] = useState(1);
    const [count, setCount] = useState(0);
    const [totalEur, setTotalEur] = useState(0);
    const [totalVatEur, setTotalVatEur] = useState(0);
    const [totalWithVatEur, setTotalWithVatEur] = useState(0);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [search, setSearch] = useState('');
    
    const restoreRowPosition = useCallback(() => {
        if (!clickedRowId.current) {
            return;
        }
        
        const rowId = clickedRowId.current;

        requestAnimationFrame(() => {
            if (!rowRefs.current[rowId]) {
                return;
            }
            
            try {
                const rowElement = rowRefs.current[rowId];
                rowElement.scrollIntoView({ 
                    behavior: 'auto', 
                    block: 'start' 
                });
            } catch (e) {
            }
        });
    }, []);
    
    useEffect(() => {
        if (invoices.length > 0 && restorePositionAfterLoad.current && clickedRowId.current) {
            const rowId = clickedRowId.current;
            
            const observer = new MutationObserver((mutations) => {
                if (rowRefs.current[rowId]) {
                    restoreRowPosition();
                    observer.disconnect();
                    restorePositionAfterLoad.current = false;
                }
            });
            
            const tableBody = document.querySelector('tbody');
            if (tableBody) {
                observer.observe(tableBody, { childList: true, subtree: true });
                
                if (rowRefs.current[rowId]) {
                    restoreRowPosition();
                    observer.disconnect();
                    restorePositionAfterLoad.current = false;
                }
            }
            return () => {
                observer.disconnect();
            };
        }
    }, [invoices, restoreRowPosition]);
    
    useEffect(() => {
        invoicesRef.current = invoices;

        if (dataLoadComplete.current && restorePositionAfterLoad.current && invoices.length > 0) {
            restorePositionAfterLoad.current = false;
            dataLoadComplete.current = false;
            
            requestAnimationFrame(() => {
                restoreRowPosition();
            });
        }
    }, [invoices, restoreRowPosition]);
    
    const getData = () => {
        if (readyToLoad) {
            setState('loading');
            
            ApiRest.getElements(moduleName, {
                page: activePage,
                options: { search, dateFrom, dateTo, ...props.options }
            }).then(res => {
                dataLoadComplete.current = true;
                
                setInvoices(res.data);
                setCount(res.count);

                setTotalEur(res.totalEur);
                setTotalVatEur(res.totalVatEur);
                setTotalWithVatEur(res.totalWithVatEur);
                setState('');
            }).catch(err => {
                setState('error');
            });
        }
    }

    useEffect(getData, [activePage, search, dateFrom, dateTo, readyToLoad]);

    const restoreHistoryState = () => {
        const params = ['search', 'activePage', 'dateFrom', 'dateTo', 'clickedRowId']
        
        if (history && history.location && history.location.state) {
            params.forEach(p => {
                // @ts-ignore
                const v = history.location.state[p];
                
                if (p === 'search' && v !== undefined) {
                    setSearch(v);
                } else if (p === 'activePage' && v !== undefined) {
                    setActivePage(v);
                } else if (p === 'dateFrom' && v !== undefined) {
                    setDateFrom(v);
                } else if (p === 'dateTo' && v !== undefined) {
                    setDateTo(v);
                } else if (p === 'clickedRowId' && v !== undefined) {
                    clickedRowId.current = v;
                    restorePositionAfterLoad.current = true;
                }
            });
        }
        setTimeout(() => {
            setReadyToLoad(true);
        }, 100);
    }
    
    useEffect(restoreHistoryState, []);

    useEffect(() => {
        if (readyToLoad) {
            setActivePage(1);
        }
    }, [search, dateFrom, dateTo]);

    const saveRowPosition = useCallback((id: string) => {
        if (!rowRefs.current[id]) {
            return null;
        }
        
        clickedRowId.current = id;
        return id;
    }, []);
    
    const handleRowClick = useCallback((item: Invoice) => {
        const rowId = `list-row-${item.id}`;
        saveRowPosition(rowId);
        
        history.replace({
            pathname: history.location.pathname,
            search: history.location.search,
            state: {
                search, 
                activePage,
                dateFrom,
                dateTo,
                clickedRowId: rowId
            }
        });
        
        history.push('/' + moduleName + '/' + item.id);
    }, [history, activePage, search, dateFrom, dateTo, saveRowPosition]);

    const setRowRef = useCallback((el: HTMLTableRowElement | null, id: string) => {
        if (el) {
            rowRefs.current[id] = el;
        }
    }, []);

    const goToNew = () => {
        history.push('/' + moduleName + '/new');
    }

    const exportInvoices = () => {
        ApiInvoice.exportMultiple({ search, dateFrom, dateTo, ...props.options });
    }

    const pages = Math.ceil(count / 20);

    const getRowNumber = (index: number) => {
        return (activePage - 1) * 20 + index + 1;
    }

    return (
        <Card className={"card-table"} ref={cardRef}>
            <Card.Header>
                <Row>
                    <Col className={"v-center"}>
                        Sąskaitos
                    </Col>
                    <Col className={"text-right"}>
                        <Button variant={"info"} onClick={exportInvoices}>Eksportuoti</Button>
                    </Col>
                </Row>
            </Card.Header>

            <Card.Header className={"search"}>
                <Row>
                    <Col className={"v-center"}>
                        <input placeholder={"Paieška"} className={"search-input"} value={search}
                            onChange={(e) => setSearch(e.target.value)} />
                    </Col>
                    <Col sm={"auto"}>
                        <Row>
                            <Col className={"v-center"}>Nuo</Col>
                            <Col sm={"8"}><DateField value={dateFrom}
                                setValue={(val) => setDateFrom(val)} /></Col>
                        </Row>
                    </Col>
                    <Col sm={"auto"}>
                        <Row>
                            <Col className={"v-center"}>Iki:</Col>
                            <Col sm={"8"}><DateField value={dateTo}
                                setValue={(val) => setDateTo(val)} /></Col>
                        </Row>
                    </Col>
                </Row>
            </Card.Header>

            <Card.Body>
                {state === 'loading' ?
                    <Spinner animation="border" variant="info" />
                    :
                    <Table striped hover className={"mt-2"}>
                        <thead>
                            <tr>
                                <NaeTable.th number={true}>Nr.</NaeTable.th>
                                <NaeTable.th link={true}>ID</NaeTable.th>
                                <NaeTable.th>Data</NaeTable.th>
                                <NaeTable.th>Numeris</NaeTable.th>
                                <NaeTable.th>Klientas</NaeTable.th>
                                <NaeTable.th number={true}>Eilučių kiekis</NaeTable.th>
                                <NaeTable.th number={true}>Iš viso</NaeTable.th>
                                <NaeTable.th number={true}>Iš viso su PVM</NaeTable.th>
                                <NaeTable.th>Valiuta</NaeTable.th>
                                <NaeTable.th number={true}>Iš viso, EUR</NaeTable.th>
                                <NaeTable.th number={true}>Iš viso su PVM, EUR</NaeTable.th>
                                <NaeTable.th number={true}> </NaeTable.th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((item: Invoice, index: number) => {
                                const rowId = `list-row-${item.id}`;
                                return (
                                    <tr 
                                        ref={(el) => setRowRef(el, rowId)}
                                        onClick={() => handleRowClick(item)} 
                                        key={rowId}
                                    >
                                        <NaeTable.td number={true}>{getRowNumber(index)}</NaeTable.td>
                                        <NaeTable.td id={true}>{item.id}</NaeTable.td>
                                        <NaeTable.td nw={true} link={true}>{item.date}</NaeTable.td>
                                        
                                        <NaeTable.td>{item.number}</NaeTable.td>
                                        <NaeTable.td small={true}>{item.client}</NaeTable.td>
                                        <NaeTable.td small={true} number={true}>{item.linesCount}</NaeTable.td>
                                        <NaeTable.td small={true}
                                            number={true}>{item.total.toFixed(2)}</NaeTable.td>
                                        <NaeTable.td small={true}
                                            number={true}>{item.totalWithVat.toFixed(2)}</NaeTable.td>
                                        <NaeTable.td small={true}>{item.currency}</NaeTable.td>
                                        <NaeTable.td number={true}>{item.totalEur.toFixed(2)}</NaeTable.td>
                                        <NaeTable.td
                                            number={true}>{item.totalWithVatEur.toFixed(2)}</NaeTable.td>
                                        <NaeTable.Td>
                                            {item.type === 'S' && item.references}
                                        </NaeTable.Td>
                                    </tr>
                                )
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={11} className={"text-right"}>Iš viso, EUR:</td>
                                <NaeTable.td
                                    number={true}>{totalEur.toFixed(2)}</NaeTable.td>
                            </tr>
                            <tr>
                                <td colSpan={11} className={"text-right"}>PVM, EUR:</td>
                                <NaeTable.td
                                    number={true}>{totalVatEur.toFixed(2)}</NaeTable.td>
                            </tr>
                            <tr>
                                <td colSpan={11} className={"text-right"}>Iš viso su PVM, EUR:</td>
                                <NaeTable.td
                                    number={true}>{totalWithVatEur.toFixed(2)}</NaeTable.td>
                            </tr>
                        </tfoot>
                    </Table>
                }
            </Card.Body>

            <Card.Footer>
                <div className={"paging"}>
                    <NaePaging.Prev onClick={() => {
                        setActivePage(Math.max(activePage - 1, 1));
                    }} />
                    <NaePaging.Group pages={pages} activePage={activePage} onClick={(p) => {
                        setActivePage(p);
                    }} />
                    <NaePaging.Next onClick={() => {
                        setActivePage(Math.min(activePage + 1, pages));
                    }} />
                </div>
            </Card.Footer>
        </Card>
    );
}